import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useNotifications } from '../../store/useStore';
import type { Database } from '../../types/database.types';
import { createInvoice, calculateInvoiceTotals } from '../../lib/invoiceUtils';
import {
  CREDITABLE_DOCUMENT_TYPES,
  remainingCreditableCents,
  toCents,
  type LinkedCreditNote,
} from '../../lib/creditNotes';
import { formatCurrency } from '../../lib/exportUtils';
import { supabase } from '../../lib/supabase';
import { Package, RotateCcw, Trash2 } from 'lucide-react';

type Customer = Database['public']['Tables']['customers']['Row'];
type Product = Database['public']['Tables']['products']['Row'];
type Invoice = Database['public']['Tables']['invoices']['Row'];

interface CreditNoteFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (creditNote: Invoice) => void;
  /** Fattura già scelta (es. aperta dal dettaglio fattura). */
  initialReferenceInvoiceId?: string | null;
}

interface InvoiceItem {
  id: string;
  product_id: string | null;
  product: Product | null;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  discount_percentage: number;
  vat_rate: number;
}

interface ReferenceOption {
  id: string;
  invoice_number: string | null;
  invoice_date: string;
  total_amount: number | null;
  customers: { company_name: string } | null;
}

type ReferenceInvoice = Invoice & { customers: Customer | null };
type ReferenceItemRow = Database['public']['Tables']['invoice_items']['Row'] & { products: Product | null };
type ReferenceInvoiceWithItems = ReferenceInvoice & { invoice_items: ReferenceItemRow[] | null };

const today = () => new Date().toISOString().slice(0, 10);

// Gli errori di Supabase sono oggetti con `message`/`details`, non sempre istanze di Error.
const errorMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === 'object') {
    const { message, details } = error as { message?: unknown; details?: unknown };
    if (typeof message === 'string' && message) return message;
    if (typeof details === 'string' && details) return details;
  }
  return fallback;
};

const optionLabel = (o: ReferenceOption) =>
  `${o.invoice_number || 'senza numero'} — ${new Date(o.invoice_date).toLocaleDateString('it-IT')} — ${o.customers?.company_name || 'Cliente'} — ${formatCurrency(o.total_amount || 0)}`;

export const CreditNoteFormModal: React.FC<CreditNoteFormModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  initialReferenceInvoiceId,
}) => {
  const { addNotification } = useNotifications();

  const [invoiceDate, setInvoiceDate] = useState(today);
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [originalItems, setOriginalItems] = useState<InvoiceItem[]>([]);
  const [status, setStatus] = useState<'draft' | 'issued'>('issued');
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState<string>('');
  const [referenceOptions, setReferenceOptions] = useState<ReferenceOption[]>([]);
  const [referenceSearch, setReferenceSearch] = useState('');
  const [selectedReferenceInvoiceId, setSelectedReferenceInvoiceId] = useState<string>('');
  const [referenceInvoice, setReferenceInvoice] = useState<ReferenceInvoice | null>(null);
  const [linkedCreditNotes, setLinkedCreditNotes] = useState<LinkedCreditNote[]>([]);
  const [creditNoteReason, setCreditNoteReason] = useState<string>('');
  const [loadingReferenceInvoice, setLoadingReferenceInvoice] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (initialReferenceInvoiceId) setSelectedReferenceInvoiceId(initialReferenceInvoiceId);

    const loadOptions = async () => {
      // Solo fatture emesse: una bozza non ha numero e non si può rettificare.
      const { data, error } = await supabase
        .from('invoices')
        .select('id, invoice_number, invoice_date, total_amount, customers(company_name)')
        .eq('status', 'issued')
        .in('document_type', [...CREDITABLE_DOCUMENT_TYPES])
        .order('invoice_date', { ascending: false })
        .limit(300);
      if (error) {
        console.error('Errore caricamento fatture di riferimento:', error);
        addNotification({ type: 'error', title: 'Errore', message: 'Impossibile caricare le fatture da rettificare' });
        return;
      }
      setReferenceOptions((data || []) as unknown as ReferenceOption[]);
    };
    loadOptions();
  }, [isOpen, initialReferenceInvoiceId, addNotification]);

  // Carica la fattura scelta: cliente, righe e note di credito già fatte su di essa
  useEffect(() => {
    const loadReferenceInvoiceData = async () => {
      if (!selectedReferenceInvoiceId) {
        setReferenceInvoice(null);
        setLinkedCreditNotes([]);
        setItems([]);
        setOriginalItems([]);
        return;
      }

      setLoadingReferenceInvoice(true);
      try {
        const [invoiceResult, linkedResult] = await Promise.all([
          supabase
            .from('invoices')
            .select(`
              *,
              customers (*),
              invoice_items (
                *,
                products (id, code, name, category, unit, base_price, photo_url, description, iva)
              )
            `)
            .eq('id', selectedReferenceInvoiceId)
            .single(),
          supabase
            .from('invoices')
            .select('taxable_amount, tax_amount, status')
            .eq('document_type', 'credit_note')
            .eq('reference_invoice_id', selectedReferenceInvoiceId),
        ]);

        if (invoiceResult.error) throw invoiceResult.error;
        if (linkedResult.error) throw linkedResult.error;

        const invoiceData = invoiceResult.data as unknown as ReferenceInvoiceWithItems;
        setReferenceInvoice(invoiceData);
        setLinkedCreditNotes((linkedResult.data || []) as LinkedCreditNote[]);

        const convertedItems: InvoiceItem[] = (invoiceData.invoice_items || []).map((item) => ({
          id: item.id,
          product_id: item.product_id,
          product: item.products,
          description: item.description || item.products?.name || '',
          quantity: item.quantity,
          unit: item.unit || item.products?.unit || 'pz',
          unit_price: item.unit_price,
          discount_percentage: item.discount_percentage || 0,
          vat_rate: item.vat_rate ?? item.products?.iva ?? 22,
        }));
        setItems(convertedItems);
        setOriginalItems(convertedItems);
      } catch (error: unknown) {
        console.error('Errore caricamento fattura di riferimento:', error);
        addNotification({
          type: 'error',
          title: 'Errore',
          message: 'Impossibile caricare i dati della fattura selezionata',
        });
      } finally {
        setLoadingReferenceInvoice(false);
      }
    };

    loadReferenceInvoiceData();
  }, [selectedReferenceInvoiceId, addNotification]);

  // La fattura passata da fuori può non essere fra le ultime 300: la aggiungo alla lista
  const visibleOptions = useMemo(() => {
    let options = referenceOptions;
    if (referenceInvoice && !options.some((o) => o.id === referenceInvoice.id)) {
      options = [
        {
          id: referenceInvoice.id,
          invoice_number: referenceInvoice.invoice_number,
          invoice_date: referenceInvoice.invoice_date,
          total_amount: referenceInvoice.total_amount,
          customers: referenceInvoice.customers ? { company_name: referenceInvoice.customers.company_name } : null,
        },
        ...options,
      ];
    }
    const q = referenceSearch.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.id === selectedReferenceInvoiceId || optionLabel(o).toLowerCase().includes(q)
    );
  }, [referenceOptions, referenceInvoice, referenceSearch, selectedReferenceInvoiceId]);

  const noVatForfettario = referenceInvoice ? referenceInvoice.no_vat_forfettario !== false : true;

  const totals = useMemo(
    () => calculateInvoiceTotals(
      items.map((item) => ({
        product_id: item.product_id,
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        unit_price: item.unit_price,
        discount_percentage: item.discount_percentage,
        vat_rate: item.vat_rate,
      })),
      noVatForfettario
    ),
    [items, noVatForfettario]
  );

  const remainingCents = referenceInvoice ? remainingCreditableCents(referenceInvoice, linkedCreditNotes) : 0;
  const alreadyCreditedCents = referenceInvoice
    ? toCents(referenceInvoice.taxable_amount) + toCents(referenceInvoice.tax_amount) - remainingCents
    : 0;
  const creditCents = toCents(totals.taxable_amount) + toCents(totals.tax_amount);
  const exceedsRemaining = !!referenceInvoice && creditCents > remainingCents;

  const updateItem = (id: string, patch: Partial<InvoiceItem>) =>
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));

  const removeItem = (id: string) => setItems((prev) => prev.filter((item) => item.id !== id));

  const resetForm = () => {
    setInvoiceDate(today());
    setItems([]);
    setOriginalItems([]);
    setStatus('issued');
    setNotes('');
    setReferenceSearch('');
    setSelectedReferenceInvoiceId('');
    setReferenceInvoice(null);
    setLinkedCreditNotes([]);
    setCreditNoteReason('');
  };

  const handleClose = () => {
    if (saving) return;
    resetForm();
    onClose();
  };

  const fail = (title: string, message: string) => addNotification({ type: 'error', title, message });

  const handleSave = async () => {
    if (!referenceInvoice || !referenceInvoice.customer_id) {
      fail('Fattura di riferimento mancante', 'Scegli la fattura che la nota di credito rettifica.');
      return;
    }
    if (!creditNoteReason.trim()) {
      fail('Motivo mancante', 'Scrivi perché emetti la nota di credito: finisce nel documento inviato al cliente.');
      return;
    }
    if (!invoiceDate || invoiceDate < referenceInvoice.invoice_date) {
      fail('Data non valida', 'La nota di credito non può avere una data precedente alla fattura che rettifica.');
      return;
    }

    const validItems = items.filter((item) => item.description.trim() && item.quantity > 0 && item.unit_price > 0);
    if (validItems.length === 0 || creditCents <= 0) {
      fail('Righe mancanti', 'Lascia almeno una riga con quantità e prezzo maggiori di zero.');
      return;
    }
    if (exceedsRemaining) {
      fail(
        'Importo troppo alto',
        `Della fattura ${referenceInvoice.invoice_number} restano da stornare ${formatCurrency(remainingCents / 100)}.`
      );
      return;
    }

    setSaving(true);
    try {
      const created = await createInvoice({
        customer_id: referenceInvoice.customer_id,
        order_id: null,
        invoice_date: invoiceDate,
        payment_terms: null,
        notes: notes.trim() || null,
        status,
        document_type: 'credit_note',
        items: validItems.map((item) => ({
          product_id: item.product_id,
          description: item.description.trim(),
          quantity: item.quantity,
          unit: item.unit,
          unit_price: item.unit_price,
          discount_percentage: item.discount_percentage,
          vat_rate: item.vat_rate,
        })),
        // Stesso regime fiscale della fattura rettificata
        no_vat_forfettario: noVatForfettario,
        no_vat_clause_text: referenceInvoice.no_vat_clause_text ?? null,
        reference_invoice_id: referenceInvoice.id,
        credit_note_reason: creditNoteReason.trim(),
      });

      addNotification({
        type: 'success',
        title: status === 'issued' ? 'Nota di credito emessa' : 'Bozza salvata',
        message: status === 'issued'
          ? `Numero ${created.invoice_number ?? 'n/d'}. Ora puoi inviarla allo SDI dal dettaglio.`
          : 'La bozza è nella pagina Note di credito.',
      });

      resetForm();
      onCreated(created);
      onClose();
    } catch (error: unknown) {
      console.error('Errore salvataggio nota di credito:', error);
      fail('Errore creazione documento', errorMessage(error, 'Si è verificato un errore durante la creazione del documento.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="flex flex-col gap-3 overflow-hidden sm:max-h-[90vh] sm:max-w-6xl">
        <DialogHeader>
          <DialogTitle>Nuova Nota di Credito</DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-2">
          {/* Fattura da rettificare */}
          <div className="space-y-4 border rounded-md p-4 bg-red-50 border-red-200">
            <h3 className="text-lg font-semibold text-red-800">Fattura da rettificare *</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Cerca</Label>
                <Input
                  value={referenceSearch}
                  onChange={(e) => setReferenceSearch(e.target.value)}
                  placeholder="Numero o cliente"
                />
              </div>
              <div className="space-y-1">
                <Label>Fattura</Label>
                <Select
                  value={selectedReferenceInvoiceId}
                  onValueChange={setSelectedReferenceInvoiceId}
                  disabled={loadingReferenceInvoice}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Scegli la fattura" />
                  </SelectTrigger>
                  <SelectContent>
                    {visibleOptions.length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">Nessuna fattura trovata</div>
                    )}
                    {visibleOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {optionLabel(option)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {loadingReferenceInvoice && (
                  <p className="text-xs text-gray-500 mt-1">Caricamento dati fattura...</p>
                )}
              </div>
            </div>

            <div className="space-y-1">
              <Label>Motivo *</Label>
              <Input
                value={creditNoteReason}
                onChange={(e) => setCreditNoteReason(e.target.value)}
                placeholder="Es: storno totale per errore di fatturazione, rimborso parziale del canone di ottobre"
                maxLength={600}
              />
              <p className="text-xs text-gray-500">Compare sul PDF e nel file inviato allo SDI.</p>
            </div>

            {referenceInvoice && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                <div className="bg-white border border-red-200 rounded p-2">
                  <div className="text-xs text-gray-500">Cliente</div>
                  <div className="font-semibold truncate">{referenceInvoice.customers?.company_name || 'N/A'}</div>
                </div>
                <div className="bg-white border border-red-200 rounded p-2">
                  <div className="text-xs text-gray-500">Già stornato</div>
                  <div className="font-semibold">{formatCurrency(alreadyCreditedCents / 100)}</div>
                </div>
                <div className="bg-white border border-red-200 rounded p-2">
                  <div className="text-xs text-gray-500">Ancora stornabile</div>
                  <div className="font-semibold">{formatCurrency(remainingCents / 100)}</div>
                </div>
              </div>
            )}
          </div>

          {/* Dati documento */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label>Data nota di credito *</Label>
              <Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Stato *</Label>
              <select
                className="w-full h-10 px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                value={status}
                onChange={(e) => setStatus(e.target.value as 'draft' | 'issued')}
              >
                <option value="issued">Emessa (prende il numero)</option>
                <option value="draft">Bozza</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>Note</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note aggiuntive (opzionale)" />
            </div>
          </div>

          {/* Righe da stornare: modificabili per il rimborso parziale */}
          {referenceInvoice && (
            <div className="space-y-3 border rounded-md p-3 bg-green-50 border-green-200">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-green-600" />
                  <Label className="mb-0 text-lg font-semibold text-green-800">Righe da stornare ({items.length})</Label>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setItems(originalItems)}
                  className="h-8 text-xs gap-1"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Ripristina righe della fattura
                </Button>
              </div>
              <p className="text-xs text-green-900">
                Per lo storno totale lascia tutto com'è. Per un rimborso parziale cambia quantità o prezzo, o togli le righe che non c'entrano.
              </p>

              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {items.length === 0 && (
                  <p className="text-sm text-gray-600 py-4 text-center">Nessuna riga: ripristina le righe della fattura.</p>
                )}
                {items.map((item) => {
                  const lineTaxable = item.quantity * item.unit_price * (1 - item.discount_percentage / 100);
                  return (
                    <div key={item.id} className="bg-white border border-green-200 rounded p-2">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3">
                        <div className="flex-1 min-w-0 space-y-1">
                          <Label className="text-[11px] text-gray-500">Descrizione</Label>
                          <Input
                            value={item.description}
                            onChange={(e) => updateItem(item.id, { description: e.target.value })}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2 sm:contents">
                          <div className="space-y-1 sm:w-24">
                            <Label className="text-[11px] text-gray-500">Q.tà ({item.unit})</Label>
                            <Input
                              type="number"
                              min={0}
                              step="any"
                              value={item.quantity}
                              onChange={(e) => updateItem(item.id, { quantity: Math.max(0, Number(e.target.value) || 0) })}
                              className="h-8 text-xs"
                            />
                          </div>
                          <div className="space-y-1 sm:w-28">
                            <Label className="text-[11px] text-gray-500">Prezzo €</Label>
                            <Input
                              type="number"
                              min={0}
                              step="0.01"
                              value={item.unit_price}
                              onChange={(e) => updateItem(item.id, { unit_price: Math.max(0, Number(e.target.value) || 0) })}
                              className="h-8 text-xs"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-2 sm:w-44 sm:justify-end">
                          <div className="text-xs text-gray-600 text-right">
                            {item.discount_percentage > 0 && <div className="text-green-700">Sconto {item.discount_percentage}%</div>}
                            {!noVatForfettario && <div>IVA {item.vat_rate}%</div>}
                            <div className="font-semibold text-gray-900">{formatCurrency(lineTaxable)}</div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeItem(item.id)}
                            className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Togli riga"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="pt-2 border-t border-green-300 text-xs text-gray-700 flex flex-wrap justify-end gap-x-4 gap-y-1">
                <div>Imponibile: {formatCurrency(totals.taxable_amount)}</div>
                {noVatForfettario ? <div>Esente IVA</div> : <div>IVA: {formatCurrency(totals.tax_amount)}</div>}
                {totals.marca_da_bollo_amount > 0 && <div>Bollo: {formatCurrency(totals.marca_da_bollo_amount)}</div>}
                <div className="font-semibold">Totale nota di credito: {formatCurrency(totals.total_amount)}</div>
              </div>
              {exceedsRemaining && (
                <p className="text-xs font-semibold text-red-700 text-right">
                  Supera quanto resta da stornare ({formatCurrency(remainingCents / 100)}).
                </p>
              )}
            </div>
          )}

          {!selectedReferenceInvoiceId && (
            <div className="text-center py-8 border rounded-md bg-gray-50">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-sm text-gray-600">Scegli una fattura per vedere le righe da stornare</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            Annulla
          </Button>
          <Button onClick={handleSave} disabled={saving || loadingReferenceInvoice || exceedsRemaining}>
            {saving ? 'Salvataggio...' : status === 'issued' ? 'Emetti nota di credito' : 'Salva bozza'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
